import { useState, useEffect, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { showToast } from '../../components/Toast';
import type { Bus } from '../../lib/types';

export function BusManager() {
  const [buses, setBuses] = useState<Bus[]>([]);
  const [drivers, setDrivers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ bus_number: '', license_plate: '', capacity: '40', driver_id: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [busesRes, driversRes] = await Promise.all([
        api.getBuses(),
        api.getUsers('driver'),
      ]);
      setBuses(busesRes.buses);
      setDrivers(driversRes.users || []);
    } catch { showToast('Failed to load buses'); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ bus_number: '', license_plate: '', capacity: '40', driver_id: '' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  const startEdit = (bus: Bus) => {
    setForm({
      bus_number: bus.bus_number,
      license_plate: bus.license_plate || '',
      capacity: String(bus.capacity || 40),
      driver_id: (bus as any).driver_id || '',
    });
    setEditingId(bus.id);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.bus_number) { setError('Bus number required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await api.updateBus(editingId, {
          bus_number: form.bus_number,
          license_plate: form.license_plate || null,
          capacity: parseInt(form.capacity) || 40,
          driver_id: form.driver_id || null,
        });
      } else {
        await api.createBus({
          bus_number: form.bus_number,
          license_plate: form.license_plate || null,
          capacity: parseInt(form.capacity) || 40,
          driver_id: form.driver_id || null,
        });
      }
      resetForm();
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (busId: string, currentActive: number) => {
    await api.updateBus(busId, { active: currentActive ? 0 : 1 });
    loadData();
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div>
      <div className="admin-section-header">
        <h2>Bus Management</h2>
        <button className="btn-sm btn-primary" onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}>
          {showForm ? 'Cancel' : '+ Add Bus'}
        </button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit} aria-label={editingId ? 'Edit bus form' : 'Add bus form'}>
          <div className="admin-form-row">
            <div className="form-group">
              <label>Bus Number</label>
              <input value={form.bus_number} onChange={(e) => setForm({ ...form, bus_number: e.target.value })} placeholder="e.g., BUS-08" required aria-label="Bus number" />
            </div>
            <div className="form-group">
              <label>License Plate</label>
              <input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} placeholder="e.g., OD-02-CD-5678" aria-label="License plate" />
            </div>
            <div className="form-group">
              <label>Capacity</label>
              <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} aria-label="Bus capacity" />
            </div>
            <div className="form-group">
              <label>Assign Driver</label>
              <select value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })} aria-label="Assign driver">
                <option value="">-- No Driver --</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} ({d.email})</option>
                ))}
              </select>
            </div>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button type="submit" className="btn-sm btn-primary" disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update Bus' : 'Create Bus'}
          </button>
          {editingId && (
            <button type="button" className="btn-sm btn-outline" style={{ marginLeft: 8 }} onClick={resetForm}>Cancel Edit</button>
          )}
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Bus Number</th>
            <th>Plate</th>
            <th>Driver</th>
            <th>Capacity</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {buses.map((bus) => (
            <tr key={bus.id}>
              <td><strong>{bus.bus_number}</strong></td>
              <td>{bus.license_plate || '--'}</td>
              <td>{bus.driver_name || <span style={{ color: '#ef4444' }}>Unassigned</span>}</td>
              <td>{bus.capacity}</td>
              <td>
                <span className={`admin-badge ${bus.active ? 'badge-green' : 'badge-red'}`}>
                  {bus.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <button className="btn-sm btn-outline" onClick={() => startEdit(bus)} style={{ marginRight: 4 }} aria-label={`Edit bus ${bus.bus_number}`}>Edit</button>
                <button className="btn-sm btn-danger" onClick={() => handleToggleActive(bus.id, bus.active ? 1 : 0)} aria-label={`${bus.active ? 'Deactivate' : 'Activate'} bus ${bus.bus_number}`}>
                  {bus.active ? 'Deactivate' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {buses.length === 0 && <div className="admin-empty">No buses yet.</div>}
    </div>
  );
}
